/**
 * Btw-behandeling voor een Nederlandse verkoper (printen.eu, Twilper, NL).
 *
 * ============================================================================
 * DE REGELS, ZOALS HIER GEIMPLEMENTEERD
 * ============================================================================
 * Dit is de plek waar de btw-beslissing valt. Niet in de browser, niet in de
 * checkoutpagina, niet in de orderroute. Alleen hier. De orderroute vraagt het
 * antwoord op en rekent ermee door.
 *
 * De verkoper zit in Nederland. Wij verkopen goederen (drukwerk), geen
 * diensten. Dat onderscheid is belangrijk: bij goederen hangt het nultarief
 * niet alleen aan de status van de afnemer, maar ook aan de vraag of de
 * goederen Nederland fysiek verlaten.
 *
 * 1. Zakelijke afnemer in een ANDERE EU-lidstaat met een GEVALIDEERD
 *    btw-nummer, en de goederen gaan naar een andere lidstaat:
 *    0%, btw verlegd (intracommunautaire levering, artikel 138 Btw-richtlijn).
 *
 * 2. Zakelijke afnemer IN NEDERLAND: gewoon Nederlandse btw, geen verlegging.
 *    Een Nederlandse ondernemer betaalt gewoon 21% en trekt die zelf af.
 *
 * 3. Afnemer BUITEN DE EU: 0% (uitvoer, artikel 146 Btw-richtlijn), zakelijk
 *    of particulier. Bepalend is waar de goederen heen gaan, niet waar de
 *    factuur heen gaat.
 *
 * 4. Consument BINNEN de EU: btw van het land van de consument, onder de
 *    OSS-regeling voor afstandsverkopen. Dat is precies wat de shop vandaag
 *    al doet via `getCountryRates(shippingCountry)`; die uitkomst wordt hier
 *    onveranderd doorgegeven.
 *
 * 5. Een btw-nummer dat NIET valideert (ongeldig, verkeerd formaat, verkeerd
 *    landprefix) of dat NIET gevalideerd KON worden (VIES plat, timeout):
 *    gewoon btw rekenen. Nooit nul rekenen op een ongecontroleerd nummer.
 *    De bestelling gaat wel door; alleen het tarief valt terug op normaal.
 *
 * ============================================================================
 * TWEE LANDEN, NIET EEN
 * ============================================================================
 * Er zijn twee verschillende landen in het spel en ze doen echt iets anders:
 *
 *   - `customerCountry` (factuuradres): waar de afnemer gevestigd is. Dit
 *     bepaalt of iemand een buitenlandse ondernemer is en of het btw-nummer
 *     bij hem hoort.
 *   - `dispatchCountry` (verzendadres): waar het pakket heen gaat. Dit
 *     bepaalt of er sprake is van uitvoer, en of de goederen Nederland
 *     daadwerkelijk verlaten.
 *
 * Daarom: een Duits bedrijf dat naar een Nederlands adres laat leveren (een
 * beursstand bijvoorbeeld, in drukwerk heel gewoon) krijgt GEEN verlegging.
 * De goederen verlaten Nederland niet, dus is het een binnenlandse levering
 * en is Nederlandse btw verschuldigd, geldig Duits btw-nummer of niet. Dat is
 * de conservatieve en juridisch juiste kant: bij twijfel btw rekenen.
 *
 * ============================================================================
 * BRUTO SHOP, NETTO ZAKELIJK
 * ============================================================================
 * Deze winkel rekent bruto. Regelprijzen worden ex btw berekend en dan
 * opgehoogd; `computeOrderTotals` krijgt bruto bedragen binnen en leidt de
 * btw er weer uit af. De hele btw-beslissing komt daarom neer op EEN getal:
 * welk `vatRate` gaat er in het `CountryRates`-object. Bij verlegging of
 * uitvoer is dat 0, en dan is het ophogen een no-op en betaalt de klant het
 * nettobedrag. Zie `applyVatTreatmentToRates` voor wat dat met verzendkosten
 * en de gratis-verzendendrempel doet.
 *
 * ============================================================================
 * NIET-FISCAAL ADVIES
 * ============================================================================
 * Dit is de standaardregelgeving zoals die voor een Nederlandse webshop met
 * EU-afnemers geldt. Laat de behandeling door een accountant bevestigen
 * voordat er echt op wordt gefactureerd.
 */

import type { CountryRates } from './rates';

/** Het land waar de verkoper gevestigd is. Alles hier is vanuit NL geredeneerd. */
export const SELLER_COUNTRY = 'NL';

/**
 * De 27 lidstaten van de EU voor btw-doeleinden. Let op de twee afwijkingen
 * ten opzichte van de ISO-landcodes:
 *   - `EL` is de btw-landcode van Griekenland, `GR` de ISO-code. Beide staan
 *     erin, want een adres draagt GR en een btw-nummer draagt EL.
 *   - `XI` is Noord-Ierland. Dat valt na de brexit voor GOEDEREN nog onder
 *     de EU-btw-regels, `GB` niet meer. Een `XI`-btw-nummer is dus geldig
 *     voor verlegging bij drukwerk; een `GB`-nummer niet.
 */
const EU_VAT_AREA = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'GR', 'ES', 'FI', 'FR',
  'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE',
  'SI', 'SK', 'XI',
]);

export function isEuVatCountry(countryCode?: string | null): boolean {
  const normalized = String(countryCode || '').trim().toUpperCase();
  if (!normalized) return false;
  return EU_VAT_AREA.has(normalized);
}

/**
 * Griekenland heeft twee codes. Een adres met `GR` en een btw-nummer met `EL`
 * horen bij elkaar en mogen niet als mismatch worden gezien.
 */
function canonicalVatCountry(countryCode: string): string {
  return countryCode === 'GR' ? 'EL' : countryCode;
}

/**
 * Lengte van het nummerdeel per lidstaat, zonder landprefix. Dit is een
 * goedkope voorcontrole die overduidelijke typefouten afvangt voordat we VIES
 * lastigvallen. Het is nadrukkelijk GEEN vervanging van VIES: een nummer met
 * de juiste lengte kan nog steeds niet bestaan. Alleen VIES beslist of een
 * nummer echt is.
 */
const VAT_NUMBER_LENGTHS: Record<string, number[]> = {
  AT: [9], BE: [10], BG: [9, 10], CY: [9], CZ: [8, 9, 10], DE: [9], DK: [8],
  EE: [9], EL: [9], ES: [9], FI: [8], FR: [11], HR: [11], HU: [8], IE: [8, 9],
  IT: [11], LT: [9, 12], LU: [8], LV: [11], MT: [8], NL: [12], PL: [10],
  PT: [9], RO: [2, 3, 4, 5, 6, 7, 8, 9, 10], SE: [12], SI: [8], SK: [10],
  XI: [5, 9, 12],
};

export type ParsedVatNumber = {
  /** Landprefix, bijvoorbeeld `DE`. Voor Griekenland altijd `EL`. */
  countryCode: string;
  /** Het nummerdeel zonder prefix, hoofdletters, zonder spaties of punten. */
  number: string;
  /** Het volledige genormaliseerde nummer, bijvoorbeeld `DE115235681`. */
  normalized: string;
};

/**
 * Maakt van wat de klant intikt een bruikbaar btw-nummer.
 *
 * Klanten typen `NL 8220.10.690.B01`, `nl822010690b01` of plakken er een
 * tab achter. Alles wat geen letter of cijfer is gaat eruit, de rest wordt
 * hoofdletter. Ontbreekt het landprefix of klopt de lengte niet, dan komt er
 * `null` uit en rekent de aanroeper gewoon btw.
 */
export function parseVatNumber(raw?: string | null): ParsedVatNumber | null {
  const cleaned = String(raw || '').replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
  if (cleaned.length < 4) return null;

  const prefix = canonicalVatCountry(cleaned.slice(0, 2));
  if (!EU_VAT_AREA.has(prefix)) return null;

  const number = cleaned.slice(2);
  if (!number) return null;

  const allowedLengths = VAT_NUMBER_LENGTHS[prefix];
  if (allowedLengths && !allowedLengths.includes(number.length)) return null;

  return { countryCode: prefix, number, normalized: `${prefix}${number}` };
}

/**
 * Uitkomst van de controle bij VIES, zoals de beslissing hem ziet.
 *
 * `unavailable` is bewust een eigen waarde en niet hetzelfde als `invalid`:
 * bij `invalid` weten we dat het nummer niet deugt, bij `unavailable` weten we
 * niets. Beide leiden tot btw rekenen, maar alleen `unavailable` is een reden
 * om de klant uit te leggen dat het aan de dienst van de EU ligt en niet aan
 * zijn nummer, en om er een melding van te maken.
 */
export type VatValidationStatus =
  | 'valid'
  | 'invalid'
  | 'malformed'
  | 'unavailable'
  | 'not-provided';

/** Waarom het tarief is wat het is. Wordt op de order vastgelegd. */
export type VatTreatmentReason =
  /** Consument in Nederland, of onbekende afnemer: gewoon Nederlandse btw. */
  | 'domestic-consumer'
  /** Nederlandse ondernemer: gewoon Nederlandse btw, geen verlegging. */
  | 'domestic-business'
  /** Consument elders in de EU: btw van zijn land, OSS-afstandsverkoop. */
  | 'oss-consumer'
  /** EU-ondernemer met gevalideerd nummer, goederen verlaten NL: verlegd. */
  | 'reverse-charge-eu-b2b'
  /** Goederen gaan buiten de EU: uitvoer, 0%. */
  | 'export-outside-eu'
  /** Zakelijk aangevinkt maar geen btw-nummer opgegeven: gewoon btw. */
  | 'business-without-vat-number'
  /** Het nummer bestaat niet volgens VIES, of het formaat deugt niet. */
  | 'vat-number-invalid'
  /** Het nummer hoort bij een ander land dan het factuuradres. */
  | 'vat-number-country-mismatch'
  /** VIES gaf geen antwoord. Wel bestellen, wel btw. */
  | 'validation-unavailable'
  /** Buitenlands bedrijf, maar de goederen blijven in Nederland. */
  | 'domestic-delivery-no-reverse-charge';

export type VatTreatment = {
  /** Het tarief dat daadwerkelijk in rekening wordt gebracht, in procenten. */
  effectiveVatRate: number;
  /** Waar not-reverse-charged 0% (uitvoer) is dit false; alleen EU-B2B is true. */
  reverseCharge: boolean;
  reason: VatTreatmentReason;
  /** Heeft de klant aangegeven zakelijk te bestellen. */
  isBusiness: boolean;
  /** Genormaliseerd btw-nummer van de klant, of null. */
  customerVatNumber: string | null;
  validationStatus: VatValidationStatus;
  /** Land dat de status van de afnemer bepaalt (factuuradres). */
  customerCountry: string;
  /** Land waar de goederen heen gaan (verzendadres). */
  dispatchCountry: string;
};

export type VatTreatmentInput = {
  /** Ruw factuurland, bijvoorbeeld `DE`. Niet door resolveOrderCountry gehaald. */
  billingCountry?: string | null;
  /** Ruw verzendland. Bepaalt uitvoer en of de goederen NL verlaten. */
  shippingCountry?: string | null;
  /** Heeft de klant "zakelijk bestellen" aangevinkt. */
  isBusiness?: boolean | null;
  /** Wat de klant intikte als btw-nummer. */
  vatNumber?: string | null;
  /** Uitkomst van de servercontrole bij VIES. Nooit iets uit de browser. */
  validationStatus?: VatValidationStatus | null;
  /** Het normale tarief van het leverland, uit `getCountryRates`. */
  baseVatRate: number;
};

/**
 * Bepaalt welke btw er op deze bestelling drukt.
 *
 * Puur: geen database, geen netwerk, geen tijd. `validationStatus` komt er als
 * gegeven in, zodat deze beslissing volledig testbaar is en zodat de
 * VIES-aanroep (traag, kan omvallen) buiten deze functie blijft.
 *
 * De volgorde van de takken is de regel zelf en mag niet worden omgezet:
 * uitvoer wint van alles, daarna pas komt de vraag of iemand ondernemer is.
 */
export function resolveVatTreatment(input: VatTreatmentInput): VatTreatment {
  const billingCountry = String(input.billingCountry || '').trim().toUpperCase();
  const shippingCountry = String(input.shippingCountry || '').trim().toUpperCase();

  // Het factuuradres bepaalt de status van de afnemer. Is het leeg, dan is het
  // verzendadres het beste dat we hebben. Is alles leeg, dan Nederland, want
  // dat is de conservatieve keuze: gewoon 21% rekenen.
  const customerCountry = billingCountry || shippingCountry || SELLER_COUNTRY;
  // Het verzendadres bepaalt waar de goederen heen gaan. Zelfde terugval.
  const dispatchCountry = shippingCountry || billingCountry || SELLER_COUNTRY;

  const baseVatRate = Number.isFinite(input.baseVatRate) && input.baseVatRate >= 0
    ? input.baseVatRate
    : 21;

  const isBusiness = input.isBusiness === true;
  const parsed = parseVatNumber(input.vatNumber);
  const customerVatNumber = parsed ? parsed.normalized : null;

  const base = {
    isBusiness,
    customerVatNumber,
    customerCountry,
    dispatchCountry,
  };

  // 1. Uitvoer. De goederen verlaten de EU, dus 0% ongeacht wie de afnemer is.
  //    Dit is geen verlegging: er is geen EU-afnemer die de btw overneemt.
  //    Let op: de shop levert vandaag alleen aan NL, BE, DE en FR, dus deze tak
  //    is in de praktijk nog onbereikbaar via de checkout. Hij staat er omdat
  //    de regel klopt zodra er een vijfde land bij komt.
  if (!isEuVatCountry(dispatchCountry)) {
    return {
      ...base,
      effectiveVatRate: 0,
      reverseCharge: false,
      reason: 'export-outside-eu',
      validationStatus: input.validationStatus || 'not-provided',
    };
  }

  // 2. Particulier (of niet als zakelijk aangemeld). Btw van het leverland.
  //    Dit is exact het gedrag van voor deze wijziging: `baseVatRate` komt uit
  //    `getCountryRates(shippingCountry)` en gaat er onveranderd weer uit.
  if (!isBusiness) {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: dispatchCountry === SELLER_COUNTRY ? 'domestic-consumer' : 'oss-consumer',
      validationStatus: 'not-provided',
    };
  }

  // 3. Zakelijk zonder btw-nummer. Kan prima: een kleine ondernemer met een
  //    KOR-vrijstelling heeft er geen. Gewoon btw rekenen, wel het PO-nummer
  //    en de bedrijfsnaam vastleggen.
  if (!input.vatNumber || String(input.vatNumber).trim() === '') {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'business-without-vat-number',
      validationStatus: 'not-provided',
    };
  }

  // 4. Wel een nummer ingetikt, maar het is geen btw-nummer. Btw rekenen.
  if (!parsed) {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'vat-number-invalid',
      validationStatus: 'malformed',
    };
  }

  // 5. Het nummer hoort bij een ander land dan waar de klant zegt te zitten.
  //    VIES valideert zo'n nummer vrolijk, want VIES kijkt alleen naar het
  //    nummer zelf. Wij mogen het niet gebruiken: het bewijst niets over deze
  //    afnemer op dit adres.
  if (parsed.countryCode !== canonicalVatCountry(customerCountry)) {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'vat-number-country-mismatch',
      validationStatus: input.validationStatus || 'not-provided',
    };
  }

  // 6. Nederlandse ondernemer. Geen verlegging, gewoon 21%. Ook niet als het
  //    btw-nummer keurig valideert: verlegging bestaat niet binnen NL voor een
  //    gewone binnenlandse levering van drukwerk.
  if (parsed.countryCode === SELLER_COUNTRY || customerCountry === SELLER_COUNTRY) {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'domestic-business',
      validationStatus: input.validationStatus || 'not-provided',
    };
  }

  // 7. Buitenlandse EU-ondernemer, maar de goederen blijven in Nederland.
  //    Geen intracommunautaire levering, dus geen nultarief. Zie de kop van
  //    dit bestand.
  if (dispatchCountry === SELLER_COUNTRY) {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'domestic-delivery-no-reverse-charge',
      validationStatus: input.validationStatus || 'not-provided',
    };
  }

  // 8. VIES kon niets zeggen. Wel bestellen, wel btw. Nooit nul op onbekend.
  if (input.validationStatus === 'unavailable') {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'validation-unavailable',
      validationStatus: 'unavailable',
    };
  }

  // 9. VIES zegt: bestaat niet. Btw rekenen.
  if (input.validationStatus !== 'valid') {
    return {
      ...base,
      effectiveVatRate: baseVatRate,
      reverseCharge: false,
      reason: 'vat-number-invalid',
      validationStatus: input.validationStatus || 'not-provided',
    };
  }

  // 10. Alles klopt: EU-ondernemer buiten NL, gevalideerd nummer op het juiste
  //     land, goederen gaan naar een andere lidstaat. Btw verlegd.
  return {
    ...base,
    effectiveVatRate: 0,
    reverseCharge: true,
    reason: 'reverse-charge-eu-b2b',
    validationStatus: 'valid',
  };
}

/**
 * Zet de btw-beslissing om in het tarievenobject waarmee de rest van de kassa
 * rekent. Dit is de enige plek die weet dat de shop bruto rekent.
 *
 * Blijft het tarief gelijk (elke consument, elke Nederlandse zakelijke klant),
 * dan komt het OORSPRONKELIJKE object er onveranderd uit. Niet een kopie, niet
 * een herberekening: hetzelfde object. Zo is met een identiteitscontrole te
 * bewijzen dat het consumentenpad geen cent verandert.
 *
 * Verandert het tarief wel (verlegging of uitvoer, dus naar 0%), dan moet het
 * bruto verzendtarief mee omlaag:
 *
 *   - `shippingCost` staat als BRUTO bedrag in de database (6,95 inclusief
 *     21%). Zou dat blijven staan bij 0% btw, dan betaalt een zakelijke klant
 *     netto 6,95 waar een consument netto 5,74 betaalt: de verzendkosten
 *     zouden voor de zakelijke klant stilletjes 21% duurder worden. Dus
 *     omrekenen naar hetzelfde nettobedrag.
 * `freeShippingFrom` blijft alleen als legacy-databaseveld bestaan en wordt
 * nergens meer voor de prijsbeslissing gebruikt.
 */
export function applyVatTreatmentToRates(
  rates: CountryRates,
  treatment: VatTreatment
): CountryRates {
  if (treatment.effectiveVatRate === rates.vatRate) return rates;

  const grossFactor = 1 + rates.vatRate / 100;
  const targetFactor = 1 + treatment.effectiveVatRate / 100;
  const convert = (grossAmount: number) => (grossAmount / grossFactor) * targetFactor;

  return {
    ...rates,
    vatRate: treatment.effectiveVatRate,
    shippingCost: Number(convert(rates.shippingCost).toFixed(2)),
  };
}

/**
 * De juridische zin die op de factuur en op de kassabon hoort te staan.
 *
 * Handgeschreven per taal, geen machinevertaling: dit is de tekst waar de
 * belastingdienst van het land van de afnemer naar kijkt. De artikelverwijzing
 * is die van Richtlijn 2006/112/EG: artikel 138 voor intracommunautaire
 * levering van goederen, artikel 146 voor uitvoer.
 */
export type VatNoticeLocale = 'nl' | 'de' | 'en' | 'fr';

const REVERSE_CHARGE_WORDING: Record<VatNoticeLocale, string> = {
  nl: 'Btw verlegd. Intracommunautaire levering van goederen, vrijgesteld op grond van artikel 138 van Richtlijn 2006/112/EG. De btw wordt verlegd naar de afnemer.',
  de: 'Steuerschuldnerschaft des Leistungsempfängers. Steuerfreie innergemeinschaftliche Lieferung gemäß Artikel 138 der Richtlinie 2006/112/EG. Die Steuerschuld geht auf den Leistungsempfänger über.',
  en: 'Reverse charge. Intra-Community supply of goods, exempt under Article 138 of Directive 2006/112/EC. VAT is to be accounted for by the customer.',
  fr: 'Autoliquidation. Livraison intracommunautaire de biens, exonérée en vertu de l’article 138 de la directive 2006/112/CE. La TVA est due par le preneur.',
};

const EXPORT_WORDING: Record<VatNoticeLocale, string> = {
  nl: 'Uitvoer buiten de EU. Vrijgesteld van btw op grond van artikel 146 van Richtlijn 2006/112/EG.',
  de: 'Ausfuhr in ein Drittland. Steuerfreie Ausfuhrlieferung gemäß Artikel 146 der Richtlinie 2006/112/EG.',
  en: 'Export outside the EU. Exempt from VAT under Article 146 of Directive 2006/112/EC.',
  fr: 'Exportation hors de l’Union européenne. Exonérée de TVA en vertu de l’article 146 de la directive 2006/112/CE.',
};

export function normalizeVatNoticeLocale(locale?: string | null): VatNoticeLocale {
  const normalized = String(locale || '').toLowerCase().split('-')[0];
  if (normalized === 'de' || normalized === 'en' || normalized === 'fr') return normalized;
  return 'nl';
}

/**
 * De zin die bij deze behandeling hoort, of `null` als er gewoon btw is
 * gerekend en er dus niets bijzonders te melden valt.
 */
export function vatLegalWording(
  treatment: Pick<VatTreatment, 'reverseCharge' | 'reason'>,
  locale?: string | null
): string | null {
  const lang = normalizeVatNoticeLocale(locale);
  if (treatment.reverseCharge) return REVERSE_CHARGE_WORDING[lang];
  if (treatment.reason === 'export-outside-eu') return EXPORT_WORDING[lang];
  return null;
}

/**
 * Uitleg voor de klant in de checkout, in gewone taal. Dit is bewust iets
 * anders dan `vatLegalWording`: de klant moet begrijpen wat er gebeurde en
 * waarom, zeker als zijn nummer NIET is geaccepteerd. Stil doorgaan met btw
 * rekenen terwijl de klant nul verwacht is het ergste dat hier kan gebeuren.
 */
const CUSTOMER_EXPLANATION: Record<VatTreatmentReason, Record<VatNoticeLocale, string>> = {
  'domestic-consumer': {
    nl: 'Nederlandse btw wordt in rekening gebracht.',
    de: 'Niederländische Mehrwertsteuer wird berechnet.',
    en: 'Dutch VAT is charged.',
    fr: 'La TVA néerlandaise est facturée.',
  },
  'domestic-business': {
    nl: 'Je bestelt zakelijk binnen Nederland. Btw verleggen kan hier niet: je betaalt gewoon Nederlandse btw en trekt die zelf af in je aangifte.',
    de: 'Sie bestellen geschäftlich innerhalb der Niederlande. Eine Verlagerung der Steuerschuld ist hier nicht möglich: die niederländische Mehrwertsteuer wird berechnet.',
    en: 'You are ordering as a business inside the Netherlands. Reverse charge does not apply here: Dutch VAT is charged and you reclaim it in your own return.',
    fr: 'Vous commandez en tant que professionnel aux Pays-Bas. L’autoliquidation ne s’applique pas: la TVA néerlandaise est facturée.',
  },
  'oss-consumer': {
    nl: 'De btw van jouw land wordt in rekening gebracht.',
    de: 'Die Mehrwertsteuer Ihres Landes wird berechnet.',
    en: 'VAT of your own country is charged.',
    fr: 'La TVA de votre pays est facturée.',
  },
  'reverse-charge-eu-b2b': {
    nl: 'Je btw-nummer is gecontroleerd en goedgekeurd. De btw wordt naar jou verlegd: je betaalt 0% btw en geeft de verwerving zelf aan.',
    de: 'Ihre Umsatzsteuer-Identifikationsnummer wurde geprüft und bestätigt. Die Steuerschuld geht auf Sie über: 0% Mehrwertsteuer, Sie melden den innergemeinschaftlichen Erwerb selbst.',
    en: 'Your VAT number has been checked and accepted. VAT is reverse-charged to you: you pay 0% VAT and account for the acquisition yourself.',
    fr: 'Votre numéro de TVA a été vérifié et accepté. La TVA est autoliquidée: 0% de TVA, vous déclarez vous-même l’acquisition.',
  },
  'export-outside-eu': {
    nl: 'Levering buiten de EU. Er wordt geen btw in rekening gebracht. Invoerrechten en btw in het land van bestemming zijn voor jouw rekening.',
    de: 'Lieferung außerhalb der EU. Es wird keine Mehrwertsteuer berechnet. Einfuhrabgaben im Bestimmungsland gehen zu Ihren Lasten.',
    en: 'Delivery outside the EU. No VAT is charged. Import duties and VAT in the destination country are yours to pay.',
    fr: 'Livraison hors UE. Aucune TVA n’est facturée. Les droits et taxes à l’importation sont à votre charge.',
  },
  'business-without-vat-number': {
    nl: 'Zonder btw-nummer kunnen we de btw niet verleggen. De btw wordt gewoon in rekening gebracht.',
    de: 'Ohne Umsatzsteuer-Identifikationsnummer können wir die Steuerschuld nicht verlagern. Die Mehrwertsteuer wird berechnet.',
    en: 'Without a VAT number we cannot reverse-charge. VAT is charged as normal.',
    fr: 'Sans numéro de TVA nous ne pouvons pas autoliquider. La TVA est facturée normalement.',
  },
  'vat-number-invalid': {
    nl: 'Dit btw-nummer is niet gevonden in het EU-register (VIES). We brengen daarom gewoon btw in rekening. Controleer het nummer en probeer het opnieuw.',
    de: 'Diese Umsatzsteuer-Identifikationsnummer wurde im EU-Register (VIES) nicht gefunden. Wir berechnen daher die Mehrwertsteuer. Bitte prüfen Sie die Nummer.',
    en: 'This VAT number was not found in the EU register (VIES). We are therefore charging VAT. Please check the number and try again.',
    fr: 'Ce numéro de TVA est introuvable dans le registre européen (VIES). La TVA est donc facturée. Vérifiez le numéro et réessayez.',
  },
  'vat-number-country-mismatch': {
    nl: 'Dit btw-nummer hoort bij een ander land dan je factuuradres. We brengen daarom gewoon btw in rekening.',
    de: 'Diese Umsatzsteuer-Identifikationsnummer gehört zu einem anderen Land als Ihre Rechnungsadresse. Wir berechnen daher die Mehrwertsteuer.',
    en: 'This VAT number belongs to a different country than your billing address. We are therefore charging VAT.',
    fr: 'Ce numéro de TVA correspond à un autre pays que votre adresse de facturation. La TVA est donc facturée.',
  },
  'validation-unavailable': {
    nl: 'De controledienst van de EU (VIES) reageert nu niet. Je bestelling gaat gewoon door, maar we brengen btw in rekening. Neem contact op als je btw-nummer wel klopt, dan corrigeren we de factuur.',
    de: 'Der Prüfdienst der EU (VIES) antwortet derzeit nicht. Ihre Bestellung geht normal weiter, aber wir berechnen Mehrwertsteuer. Melden Sie sich, wenn Ihre Nummer korrekt ist, dann korrigieren wir die Rechnung.',
    en: 'The EU checking service (VIES) is not responding right now. Your order goes through as normal, but VAT is charged. Contact us if your VAT number is correct and we will correct the invoice.',
    fr: 'Le service de vérification de l’UE (VIES) ne répond pas actuellement. Votre commande se poursuit normalement, mais la TVA est facturée. Contactez-nous si votre numéro est correct et nous corrigerons la facture.',
  },
  'domestic-delivery-no-reverse-charge': {
    nl: 'De goederen worden in Nederland geleverd, dus verlaten ze Nederland niet. Verleggen kan dan niet en er wordt Nederlandse btw in rekening gebracht.',
    de: 'Die Ware wird innerhalb der Niederlande geliefert und verlässt die Niederlande somit nicht. Eine Verlagerung der Steuerschuld ist dann nicht möglich, es wird niederländische Mehrwertsteuer berechnet.',
    en: 'The goods are delivered inside the Netherlands, so they do not leave the country. Reverse charge cannot apply and Dutch VAT is charged.',
    fr: 'Les biens sont livrés aux Pays-Bas et ne quittent donc pas le pays. L’autoliquidation ne peut pas s’appliquer et la TVA néerlandaise est facturée.',
  },
};

export function vatCustomerExplanation(
  treatment: Pick<VatTreatment, 'reason'>,
  locale?: string | null
): string {
  const lang = normalizeVatNoticeLocale(locale);
  return CUSTOMER_EXPLANATION[treatment.reason][lang];
}
